import React, { useState, useEffect } from 'react';
import { Trophy, Users, Ticket, Award, Search, ExternalLink, Calendar, Clock } from 'lucide-react';
import api, { formatApiErrorDetail } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const Homepage = () => {
  const [settings, setSettings] = useState({});
  const [branding, setBranding] = useState({});
  const [prizes, setPrizes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [topEntries, setTopEntries] = useState([]);
  const [sugoId, setSugoId] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    loadSettings();
    loadBranding();
    loadPrizes();
    loadWinners();
    loadStatistics();
    loadTopEntries();
  }, []);

  useEffect(() => {
    if (settings.live_draw_date) {
      const timer = setInterval(() => {
        calculateCountdown(settings.live_draw_date);
      }, 1000);
      return () => clearInterval(timer);
      <style>{`
        @keyframes ceosTopFiveMarquee {
          0% {
            transform: translateX(0);
          }

          100% {
            transform: translateX(-50%);
          }
        }

        .ceos-led-marquee {
          width: 100%;
          overflow: hidden;
          background: transparent;
          border: 0;
          padding: 12px 0;

          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            black 8%,
            black 92%,
            transparent 100%
          );

          mask-image: linear-gradient(
            to right,
            transparent 0%,
            black 8%,
            black 92%,
            transparent 100%
          );
        }

        .ceos-led-track {
          display: flex;
          align-items: center;
          width: max-content;
          gap: 64px;
          animation: ceosTopFiveMarquee 32s linear infinite;
          will-change: transform;
        }

        .ceos-led-item {
          display: flex;
          flex-shrink: 0;
          align-items: center;
          gap: 12px;
          white-space: nowrap;
          text-transform: uppercase;

          font-family: "Arial Black", "Manrope", sans-serif;
          font-size: 21px;
          font-weight: 900;
          letter-spacing: 2px;

          color: transparent;

          background-image:
            radial-gradient(
              circle,
              #fff8ce 0,
              #ffd45a 27%,
              #f6a900 47%,
              transparent 52%
            );

          background-size: 7px 7px;

          -webkit-background-clip: text;
          background-clip: text;

          filter:
            drop-shadow(0 0 2px rgba(255, 235, 150, 0.95))
            drop-shadow(0 0 5px rgba(255, 187, 35, 0.85));
        }

        .ceos-led-rank,
        .ceos-led-ticket {
          color: transparent;

          background-image:
            radial-gradient(
              circle,
              #ffffff 0,
              #ffb4da 26%,
              #ff5bae 47%,
              transparent 52%
            );

          background-size: 7px 7px;

          -webkit-background-clip: text;
          background-clip: text;

          filter:
            drop-shadow(0 0 2px rgba(255, 210, 235, 0.95))
            drop-shadow(0 0 6px rgba(255, 67, 157, 0.9));
        }

        .ceos-led-separator {
          color: #f7c83d;
          text-shadow:
            0 0 4px rgba(247, 200, 61, 0.9),
            0 0 9px rgba(247, 200, 61, 0.65);
        }

        @media (max-width: 640px) {
          .ceos-led-marquee {
            padding: 8px 0;
          }

          .ceos-led-track {
            gap: 42px;
            animation-duration: 38s;
          }

          .ceos-led-item {
            gap: 8px;
            font-size: 15px;
            letter-spacing: 1.2px;
            background-size: 5px 5px;
          }

          .ceos-led-rank,
          .ceos-led-ticket {
            background-size: 5px 5px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ceos-led-track {
            animation-play-state: paused;
          }
        }
      `}</style>

    }
  }, [settings.live_draw_date]);

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadBranding = async () => {
    try {
      const { data } = await api.get('/settings/branding');
      setBranding(data?.value || data || {});
    } catch (error) {
      console.error('Failed to load branding:', error);
    }
  };

  const loadPrizes = async () => {
    try {
      const { data } = await api.get('/prizes?active_only=true');
      setPrizes(data);
    } catch (error) {
      console.error('Failed to load prizes:', error);
    }
  };

  const loadWinners = async () => {
    try {
      const { data } = await api.get('/public/winners');
      setWinners(data);
    } catch (error) {
      console.error('Failed to load winners:', error);
    }
  };

  const loadTopEntries = async () => {
    try {
      const { data } = await api.get('/public/top-entries');
      setTopEntries(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to load top entries:', error);
      setTopEntries([]);
    }
  };

  const loadStatistics = async () => {
    try {
      const { data } = await api.get('/public/statistics');
      setStatistics(data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const calculateCountdown = (targetDate) => {
    const now = new Date().getTime();
    const target = new Date(targetDate).getTime();
    const difference = target - now;

    if (difference > 0) {
      setCountdown({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      });
    } else {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  };

  const handleCheckEntry = async () => {
    if (!sugoId.trim()) {
      toast.error('Please enter your Sugo ID');
      return;
    }

    setSearching(true);
    setSearchResult(null);

    try {
      const { data } = await api.get(`/public/check-entry/${sugoId.trim()}`);
      setSearchResult(data);
      toast.success('Entry found!');
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || 'Entry not found';
      toast.error(errorMessage);
      setSearchResult(null);
    } finally {
      setSearching(false);
    }
  };

  const maskId = (id) => {
    if (id && id.length > 6) {
      return `${id.slice(0, 3)}****${id.slice(-3)}`;
    }
    return id;
  };

  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const formatCampaignPeriod = (start, end) => {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return `${start} – ${end}`;
    const sameYear = s.getFullYear() === e.getFullYear();
    const startStr = `${s.getDate()} ${MONTH_NAMES[s.getMonth()]}${sameYear ? '' : ' ' + s.getFullYear()}`;
    const endStr = `${e.getDate()} ${MONTH_NAMES[e.getMonth()]} ${e.getFullYear()}`;
    return `${startStr} – ${endStr}`;
  };

  const formatLiveDrawDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day = d.getDate();
    const month = MONTH_NAMES[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minStr = minutes === 0 ? '00' : String(minutes).padStart(2, '0');
    return `${day} ${month} ${year} • ${hours}:${minStr} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      {/* Hero Section */}
      <section
        className="relative py-10 px-4 sm:py-24 overflow-hidden"
        style={
          settings.hero?.image_enabled && (settings.hero?.desktop_image_url || settings.hero?.background_image_url)
            ? {
                backgroundImage: `url("${settings.hero?.desktop_image_url || settings.hero?.background_image_url}")`,
                backgroundSize: settings.hero?.bg_size || 'cover',
                backgroundPosition: settings.hero?.bg_position || 'center',
                backgroundRepeat: 'no-repeat',
              }
            : undefined
        }
        data-testid="hero-section"
      >
        {/* Mobile-only image (only rendered when image is enabled and a mobile URL is set) */}
        {settings.hero?.image_enabled && settings.hero?.mobile_image_url && (
          <div
            aria-hidden="true"
            className="absolute inset-0 sm:hidden"
            style={{
              backgroundImage: `url("${settings.hero.mobile_image_url}")`,
              backgroundSize: settings.hero?.bg_size || 'cover',
              backgroundPosition: settings.hero?.bg_position || 'center',
              backgroundRepeat: 'no-repeat',
            }}
            data-testid="hero-mobile-bg"
          />
        )}
        {/* Overlay (only when image enabled) */}
        {settings.hero?.image_enabled && (settings.hero?.desktop_image_url || settings.hero?.mobile_image_url || settings.hero?.background_image_url) && (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              backgroundColor: settings.hero?.overlay_color || '#080808',
              opacity: typeof settings.hero?.overlay_opacity === 'number' ? settings.hero.overlay_opacity : 0.6,
            }}
            data-testid="hero-overlay"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 via-[#080808] to-[#080808] pointer-events-none"></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src={branding.logo_url}
            alt="CEOs Family"
            className="h-28 w-28 sm:h-44 sm:w-44 object-contain rounded-full"
            data-testid="branding-logo"
          />
        </div>

        <div className="inline-block px-4 py-1 mb-4 sm:mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#F3A6C8] bg-[#121212] border border-[#F3A6C8]/30 rounded-full" data-testid="event-label">
            {settings.room_id && `Room ID: ${settings.room_id}`}
          </div>
          <h1 className="text-[40px] leading-[1.1] sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-3 sm:mb-4 break-words" data-testid="event-title">
            {settings.hero?.title || "CEOs FAMILY LEADER'S BIRTHDAY"}
          </h1>
          <h2 className="text-[28px] leading-[1.15] sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#D4AF37] mb-3 sm:mb-6" data-testid="event-subtitle">
            {settings.hero?.subtitle || "& TOP-UP LUCKY DRAW"}
          </h2>
          <p className="text-[48px] leading-none sm:text-6xl font-bold text-white mb-4 sm:mb-8" data-testid="event-year">
            {settings.hero?.year || "2026"}
          </p>
          <p className="text-sm sm:text-lg text-[#A0A0A0] mb-6 sm:mb-12 max-w-[280px] sm:max-w-2xl mx-auto leading-snug" data-testid="event-tagline">
            {settings.hero?.tagline || settings.hero_tagline || 'EVERY TOP-UP GIVES YOU MORE CHANCES TO WIN'}
          </p>

          {/* Countdown */}
          {(settings.countdown?.enabled !== false && settings.show_countdown !== false) && (
            <div
              className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 sm:gap-4 mb-6 sm:mb-12 max-w-[300px] sm:max-w-none mx-auto"
              data-testid="countdown-container"
            >
              {[
                { label: settings.countdown?.labels?.days || 'DAYS', value: countdown.days, key: 'days' },
                { label: settings.countdown?.labels?.hours || 'HOURS', value: countdown.hours, key: 'hours' },
                { label: settings.countdown?.labels?.minutes || 'MINUTES', value: countdown.minutes, key: 'minutes' },
                { label: settings.countdown?.labels?.seconds || 'SECONDS', value: countdown.seconds, key: 'seconds' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-[#121212] border border-[#D4AF37]/20 rounded-2xl px-3 py-3 sm:p-6 sm:min-w-[100px] w-full sm:w-auto flex flex-col items-center justify-center"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-[#D4AF37]" data-testid={`countdown-${item.key}`}>
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-[#A0A0A0] mt-1 sm:mt-2">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3 sm:gap-4 max-w-[320px] sm:max-w-none mx-auto">
            <Button
              onClick={() => document.getElementById(settings.hero?.button_check_target || 'check-entries').scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto min-h-[48px] px-6 py-3 sm:px-8 sm:py-6 rounded-full bg-[#D4AF37] text-[#080808] font-bold text-base sm:text-lg hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200 shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:shadow-[0_0_30px_rgba(243,166,200,0.6)]"
              data-testid="btn-check-entries"
            >
              <Search className="w-5 h-5 mr-2" />
              {settings.hero?.button_check_text || 'CHECK MY ENTRIES'}
            </Button>
            <Button
              onClick={() => window.open(settings.topup_url || '#', '_blank')}
              className="w-full sm:w-auto min-h-[48px] px-6 py-3 sm:px-8 sm:py-6 rounded-full bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] font-bold text-base sm:text-lg hover:bg-[#D4AF37]/10 transition-colors duration-200"
              data-testid="btn-topup-now"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {settings.hero?.button_topup_text || 'TOP UP NOW'}
            </Button>
          </div>

          {/* Event Dates */}
          <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-2 sm:gap-8 text-[#A0A0A0] text-[13px] sm:text-base">
            <div className="flex items-center gap-2" data-testid="campaign-period">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
              <span>Campaign: {formatCampaignPeriod(settings.campaign_start, settings.campaign_end)}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="live-draw-date">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#F3A6C8] flex-shrink-0" />
              <span>Live Draw: {formatLiveDrawDate(settings.live_draw_date)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {statistics && settings.statistics?.enabled !== false && (
        <section className="py-12 px-4 bg-[#121212]/50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'total_participants', icon: Users, label: settings.statistics?.labels?.total_participants || 'Total Participants', value: statistics.total_participants, color: '#D4AF37' },
                { key: 'total_entries', icon: Ticket, label: settings.statistics?.labels?.total_entries || 'Total Entries', value: statistics.total_entries, color: '#F3A6C8' },
                { key: 'remaining_entries', icon: Ticket, label: settings.statistics?.labels?.remaining_entries || 'Remaining Entries', value: statistics.remaining_entries, color: '#D4AF37' },
                { key: 'total_winners', icon: Trophy, label: settings.statistics?.labels?.total_winners || 'Total Winners', value: statistics.total_winners, color: '#F3A6C8' },
              ].filter((s) => settings.statistics?.visibility?.[s.key] !== false).map((stat, index) => (
                <Card key={index} className="bg-[#121212] border border-[#D4AF37]/10 p-6 text-center" data-testid={`stat-${stat.key.replace(/_/g, '-')}`}>
                  <stat.icon className="w-8 h-8 mx-auto mb-3" style={{ color: stat.color }} />
                  <div className="text-3xl font-bold" style={{ color: stat.color }}>
                    {stat.value.toLocaleString()}
                  </div>
                  <div className="text-xs uppercase tracking-wide text-[#A0A0A0] mt-2">
                    {stat.label}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Public Top 5 Premium Marquee */}
      {topEntries.length > 0 && (
        <section
          className="px-4 mt-9 mb-14 sm:mt-10 sm:mb-16"
          data-testid="top-entries-marquee-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="ceos-top5-v3-board">
              <div className="ceos-top5-v3-label">
                <span className="ceos-top5-v3-star">✦</span>
                <span>TOP 5</span>
                <span className="ceos-top5-v3-star">✦</span>
              </div>

              <div className="ceos-top5-v3-viewport">
                <div className="ceos-top5-v3-track">
                  {[0, 1].map((copyIndex) => (
                    <div
                      key={`top5-v3-copy-${copyIndex}`}
                      className="ceos-top5-v3-group"
                      aria-hidden={copyIndex === 1}
                    >
                      {topEntries.map((item) => (
                        <div
                          key={`${copyIndex}-${item.sugo_id}`}
                          className="ceos-top5-v3-item"
                        >
                          <span className="ceos-top5-v3-rank">
                            #{item.rank}
                          </span>

                          {item.username && (
                            <span className="ceos-top5-v3-name">
                              {item.username}
                            </span>
                          )}

                          <span className="ceos-top5-v3-dot">•</span>

                          <span className="ceos-top5-v3-id">
                            ID {item.sugo_id}
                          </span>

                          <span className="ceos-top5-v3-dot">•</span>

                          <span className="ceos-top5-v3-tickets">
                            {item.total_entries}{' '}
                            {item.total_entries === 1 ? 'TICKET' : 'TICKETS'}
                          </span>

                          <span className="ceos-top5-v3-end">✦</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="ceos-top5-v3-caption">
              Highest Lucky Draw Entry Holders
            </p>
          </div>
        </section>
      )}

      {/* Check Entry Section */}
      <section id="check-entries" className="py-24 px-4" data-testid="check-entries-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4">
            {settings.entry_checker?.section_title || 'Check Your Entries'}
          </h2>
          <p className="text-center text-[#A0A0A0] mb-8">
            {settings.entry_checker?.instruction || 'Enter your Sugo ID to verify your Lucky Draw entries'}
          </p>

          <div className="flex gap-3 mb-8">
            <Input
              type="text"
              placeholder={settings.entry_checker?.placeholder || 'Enter your Sugo ID'}
              value={sugoId}
              onChange={(e) => setSugoId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCheckEntry()}
              className="flex-1 bg-[#121212] border-[#D4AF37]/30 text-white placeholder:text-[#A0A0A0] h-14 text-lg"
              data-testid="input-sugo-id"
            />
            <Button
              onClick={handleCheckEntry}
              disabled={searching}
              className="px-8 h-14 rounded-full bg-[#D4AF37] text-[#080808] font-bold hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200"
              data-testid="btn-search-entry"
            >
              {searching ? 'Searching...' : (settings.entry_checker?.search_button || 'Search')}
            </Button>
          </div>

          {searchResult && (
            <Card className="bg-[#121212] border border-[#D4AF37]/20 p-8" data-testid="search-result-card">
              <div className="text-center mb-6">
                <Badge className="bg-[#D4AF37] text-[#080808] text-sm px-4 py-2">
                  Successfully Entered
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center" data-testid="result-sugo-id">
                  <div className="text-[#A0A0A0] text-sm mb-1">Sugo ID</div>
                  <div className="text-white text-xl font-bold">{searchResult.sugo_id}</div>
                </div>
                {searchResult.username && (
                  <div className="text-center" data-testid="result-username">
                    <div className="text-[#A0A0A0] text-sm mb-1">Username</div>
                    <div className="text-white text-xl font-bold">{searchResult.username}</div>
                  </div>
                )}
                <div className="text-center" data-testid="result-total-entries">
                  <div className="text-[#A0A0A0] text-sm mb-1">Total Entries</div>
                  <div className="text-[#D4AF37] text-2xl font-bold">{searchResult.total_entries}</div>
                </div>
                <div className="text-center" data-testid="result-active-entries">
                  <div className="text-[#A0A0A0] text-sm mb-1">Active Chances</div>
                  <div className="text-[#F3A6C8] text-2xl font-bold">{searchResult.active_entries}</div>
                </div>
                <div className="text-center" data-testid="result-used-entries">
                  <div className="text-[#A0A0A0] text-sm mb-1">Used Entries</div>
                  <div className="text-white text-2xl font-bold">{searchResult.used_entries}</div>
                </div>
                <div className="text-center" data-testid="result-wins">
                  <div className="text-[#A0A0A0] text-sm mb-1">Wins</div>
                  <div className="text-[#D4AF37] text-2xl font-bold">{searchResult.wins}</div>
                </div>
              </div>

              {searchResult.entries && searchResult.entries.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">Entry History</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="entry-history-list">
                    {searchResult.entries.map((entry, index) => (
                      <div key={index} className="flex justify-between items-center bg-[#080808] p-3 rounded-lg border border-[#D4AF37]/10">
                        <span className="text-[#A0A0A0] text-sm">
                          {new Date(entry.entry_date).toLocaleString()}
                        </span>
                        <Badge
                          className={`text-xs ${
                            entry.status === 'Active'
                              ? 'bg-[#D4AF37] text-[#080808]'
                              : 'bg-[#121212] text-[#A0A0A0] border border-[#A0A0A0]/30'
                          }`}
                        >
                          {entry.status}
                        </Badge>
                        {entry.prize_won && (
                          <span className="text-[#F3A6C8] text-sm font-semibold">
                            Won: {entry.prize_won}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </section>

      {/* Prize Section */}
      <section className="py-24 px-4 bg-[#121212]/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4">
            {settings.prizes_section?.section_title || 'Prizes to Win'}
          </h2>
          <p className="text-center text-[#A0A0A0] mb-12">
            {settings.prizes_section?.instruction || 'Amazing prizes await our lucky winners'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="prizes-grid">
            {prizes.map((prize, index) => (
              <Card
                key={prize.id}
                className="bg-[#121212] border border-[#D4AF37]/10 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:border-[#D4AF37]/40 hover:shadow-[0_8px_32px_rgba(212,175,55,0.2)]"
                data-testid={`prize-card-${index}`}
              >
                <div className="aspect-video bg-gradient-to-br from-[#D4AF37]/20 to-[#F3A6C8]/20 relative overflow-hidden">
                  <img
                    src={prize.image_url}
                    alt={prize.name}
                    className="w-full h-full object-cover"
                  />
                  {settings.prizes_section?.show_remaining !== false && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-[#D4AF37] text-[#080808] text-xs px-3 py-1">
                        {prize.remaining_quantity} {settings.prizes_section?.left_label || 'left'}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{prize.name}</h3>
                  <p className="text-[#A0A0A0] text-sm">{prize.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How to Join Section */}
      <section className="py-24 px-4" data-testid="how-to-join-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-12">
            {settings.how_to_join?.section_title || 'How to Join'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {(settings.how_to_join?.steps || [])
              .filter((s) => s.enabled !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item, index) => (
                <div key={index} className="text-center" data-testid={`how-to-join-step-${index + 1}`}>
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#080808] text-2xl font-bold mx-auto mb-4">
                    {item.order || index + 1}
                  </div>
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-[#A0A0A0] text-sm">{item.description}</p>
                </div>
              ))}
          </div>
          {settings.how_to_join?.clarification && (
            <p className="mt-10 max-w-3xl mx-auto text-center text-[#A0A0A0] text-sm leading-relaxed" data-testid="how-to-join-clarification">
              {settings.how_to_join.clarification}
            </p>
          )}
        </div>
      </section>

      {/* Rules Section */}
      <section className="py-24 px-4 bg-[#121212]/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-12">
            {settings.rules_section?.section_title || 'Rules & Terms'}
          </h2>
          <div className="space-y-4">
            {(settings.rules_section?.items || (settings.rules || []).map((r, i) => ({ text: r, enabled: true, order: i + 1 })))
              .filter((r) => r.enabled !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((rule, index) => (
                <div key={index} className="flex items-start gap-4 bg-[#121212] border border-[#D4AF37]/10 rounded-lg p-4">
                  <div className="w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#080808] text-sm font-bold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <p className="text-[#A0A0A0]">{rule.text || rule}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Winners Section */}
      {winners.length > 0 && settings.winners_section?.visibility !== false && (
        <section className="py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-12">
              <Trophy className="w-10 h-10 inline-block mr-3 text-[#D4AF37]" />
              {settings.winners_section?.section_title || 'Lucky Winners'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="winners-list">
              {winners.slice(0, settings.winners_section?.per_page || 12).map((winner, index) => (
                <Card key={index} className="bg-[#121212] border border-[#F3A6C8]/20 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-[#F3A6C8] text-[#080808] text-xs">
                      {settings.winners_section?.winner_prefix || 'Winner'} #{index + 1}
                    </Badge>
                    {settings.winners_section?.show_draw_date !== false && (
                      <span className="text-[#A0A0A0] text-xs">
                        {new Date(winner.draw_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {settings.winners_section?.show_username !== false && (
                    <div className="text-white font-bold mb-1">
                      {winner.username || 'Anonymous'}
                    </div>
                  )}
                  <div className="text-[#A0A0A0] text-sm mb-2">
                    ID: {winner.sugo_id}
                  </div>
                  {settings.winners_section?.show_prize !== false && (
                    <div className="text-[#D4AF37] font-semibold">
                      {winner.prize}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#121212] border-t border-[#D4AF37]/20">
        {settings.footer?.logo_url && (
          <div className="flex justify-center mb-5">
            <img
              src={settings.footer.logo_url}
              alt="CEOs Family"
              className="h-20 w-20 object-contain rounded-full"
              data-testid="footer-logo"
            />
          </div>
        )}
        <div className="max-w-6xl mx-auto text-center text-[#A0A0A0]">
          <p className="mb-4">
            {settings.footer?.copyright || '© 2026 CEOs Family. All rights reserved.'}
          </p>
          <p className="text-sm">
            {settings.footer?.contact_label || 'For inquiries, contact us via'}{' '}
            <a
              href={settings.footer?.whatsapp_url || settings.contact_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:text-[#F3A6C8]"
              data-testid="footer-whatsapp-link"
            >
              {settings.footer?.whatsapp_label || 'WhatsApp'}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;